import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Avatar,
  Stack,
  LinearProgress,
  Chip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import apiEndpoints from "../../../apiconfig";

export default function ProgressTrackingPage() {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
const [classDataMap, setClassDataMap] = useState({});

  const [role] = useState(() => {
    try {
      return localStorage.getItem("role") || "staff";
    } catch {
      return "staff";
    }
  });

  const [user] = useState(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [selectedClassGuid, setSelectedClassGuid] = useState("");
  const [data, setData] = useState({
    branches: [],
    classes: [],
    students: [],
    activities: [],
    activity_student_status: [],
    student_attendance: [],
  });
  const ratingToMarks = (rating) => {
    const map = {
      1: 20,
      2: 40,
      3: 60,
      4: 80,
      5: 100,
    };
    return map[Number(rating)] ?? null;
  };

  // ---------------------------
  // Normalize assigned_class into array (robust)
  // ---------------------------
  const assignedClassArray = useMemo(() => {
    if (!user || user.assigned_class == null) return [];
    const raw = user.assigned_class;
    if (Array.isArray(raw)) return raw.map(String);
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.map(String);
      } catch (e) {
        return raw.split(",").map((x) => x.trim());
      }
    }
    return [];
  }, [user]);
  useEffect(() => {
    fetch(
      `${apiEndpoints.progresstracking}?` +
        new URLSearchParams({
          role,
          assigned_classes: assignedClassArray.join(","),
        })
    )
      .then((res) => res.json())
      .then((res) => {
        setData((prev) => ({
          ...prev,
          classes: res.classes || [],
        }));
      })
      .catch(console.error);
  }, [role, assignedClassArray]);

useEffect(() => {
  if (!selectedClassGuid) return;

  fetch(
    `${apiEndpoints.progresstracking}?` +
      new URLSearchParams({
        role,
        assigned_classes: assignedClassArray.join(","),
        class_guid: selectedClassGuid,
      })
  )
    .then((res) => res.json())
    .then((res) => {
      setClassDataMap((prev) => ({
        ...prev,
        [selectedClassGuid]: {
          students: res.student_crud || [],
          activities: res.activities || [],
          activity_student_status: res.activity_student_status || [],
          student_attendance: res.student_attendance || [],
        },
      }));
    });
}, [selectedClassGuid]);



  // ---------------------------
  // Visible classes (mock-only)
  // ---------------------------
  const visibleClasses = useMemo(() => {
    return data.classes || [];
  }, [data.classes]);

  // set default selected class
useEffect(() => {
  if (visibleClasses.length === 0) return;

  visibleClasses.forEach((cls) => {
    // skip if already loaded
    if (classDataMap[cls.guid]) return;

    fetch(
      `${apiEndpoints.progresstracking}?` +
        new URLSearchParams({
          role,
          assigned_classes: assignedClassArray.join(","),
          class_guid: cls.guid,
        })
    )
      .then((res) => res.json())
      .then((res) => {
        setClassDataMap((prev) => ({
          ...prev,
          [cls.guid]: {
            students: res.student_crud || [],
            activities: res.activities || [],
            activity_student_status: res.activity_student_status || [],
            student_attendance: res.student_attendance || [],
          },
        }));
      });
  });
}, [visibleClasses]);


  // ---------------------------
  // Derived progress data
  // ---------------------------
const classesWithProgress = useMemo(() => {
  return visibleClasses.map((c) => {
    const classData = classDataMap[c.guid] || {
      students: [],
      activities: [],
      activity_student_status: [],
      student_attendance: [],
    };

    const classStudents = classData.students;
    const classActivities = classData.activities;

    const totalActivitySlots = classActivities.length * classStudents.length;

    const completedActivitySlots = classData.activity_student_status.filter(
      (st) => Number(st.is_completed) === 1
    ).length;

    const activityPercent =
      totalActivitySlots === 0
        ? 0
        : Math.round((completedActivitySlots / totalActivitySlots) * 100);

    const attendanceTotal = classData.student_attendance.length;
    const attendancePresent = classData.student_attendance.filter(
      (att) => String(att.status).toLowerCase() === "present"
    ).length;

    const attendancePercent =
      attendanceTotal === 0
        ? 0
        : Math.round((attendancePresent / attendanceTotal) * 100);

        const marks = classData.activity_student_status
          .filter((st) => st.rating > 0)
          .map((st) => ratingToMarks(st.rating))
          .filter((m) => m !== null);

        const avgMarks = marks.length
          ? +(marks.reduce((a, b) => a + b, 0) / marks.length).toFixed(1)
          : null;


   return {
     ...c,
     totalStudents: classStudents.length,
     totalActivities: classActivities.length,
     attendancePercent,
     activityPercent,
     avgMarks,
   };

  });
}, [visibleClasses, classDataMap]);

 const selectedClass =
   data.classes.find((c) => c.guid === selectedClassGuid) || null;
  // selected class objects
const selectedClassData = classDataMap[selectedClassGuid] || {
  students: [],
  activities: [],
  activity_student_status: [],
  student_attendance: [],
};

const selectedClassStudents = selectedClassData.students;
const selectedClassActivities = selectedClassData.activities;


  // per-student progress
  const studentsWithProgress = useMemo(() => {
    return selectedClassStudents.map((s) => {
      const totalActs = selectedClassActivities.length;
      const completedActs = selectedClassData.activity_student_status.filter(
        (st) =>
          st.student_guid === s.guid &&
          selectedClassActivities.some((a) => a.guid === st.activity_guid) &&
          Number(st.is_completed) === 1
      ).length;
      const activityPercent =
        totalActs === 0 ? 0 : Math.round((completedActs / totalActs) * 100);

      const attRecords = selectedClassData.student_attendance.filter(
        (att) =>
          att.student_guid === s.guid && att.class_guid === selectedClass?.guid
      );
      const totalAtt = attRecords.length;
      const presentCount = attRecords.filter(
        (att) => String(att.status).toLowerCase() === "present"
      ).length;
      const attendancePercent =
        totalAtt === 0 ? 0 : Math.round((presentCount / totalAtt) * 100);

      const marks = selectedClassData.activity_student_status
        .filter((st) => st.student_guid === s.guid && st.rating > 0)
        .map((x) => ratingToMarks(x.rating))
        .filter((m) => m !== null);

      const avgMarks = marks.length
        ? +(marks.reduce((a, b) => a + b, 0) / marks.length).toFixed(1)
        : null;

      return {
        ...s,
        totalActivities: totalActs,
        completedActivities: completedActs,
        activityPercent,
        attendancePercent,
        avgMarks,
      };
    });
  }, [selectedClassGuid, selectedClassData]);


  // small Progress component
  const Progress = ({ value = 0 }) => (
    <LinearProgress
      variant="determinate"
      value={Math.max(0, Math.min(100, value))}
      sx={{ height: 10, borderRadius: 99 }}
    />
  );

  // glass card style
  const glassCardSx = {
    p: 2,
    borderRadius: 2,
    background: "rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.4)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
    borderRadius: 3,
    // transition: "transform .18s ease, box-shadow .18s ease",
    // "&:hover": {
    //   transform: "translateY(-6px)",
    //   boxShadow: "0 10px 30px rgba(15,23,42,0.12)",
    // },
  };

  // ===== Anchor menu state (per-student) =====
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuStudent, setMenuStudent] = useState(null);
  const openMenu = Boolean(menuAnchorEl);

  const handleOpenMenu = (event, student) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuStudent(student);
  };
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuStudent(null);
  };

  // ===== Modal state =====
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(""); // 'attendance' | 'activities' | 'summary'
  const [modalStudent, setModalStudent] = useState(null);

  const openModalFor = (type) => {
    setModalType(type);
    setModalStudent(menuStudent);
    setModalOpen(true);
    handleCloseMenu();
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalType("");
    setModalStudent(null);
  };

  // helpers for modal content
  const getAttendanceRecords = (studentGuid) =>
    selectedClassData.student_attendance.filter(
      (a) =>
        a.student_guid === studentGuid &&
        (!selectedClass || selectedClass.guid === a.class_guid)
    );
  const getActivityStatuses = (studentGuid) =>
    selectedClassData.activity_student_status.filter(
      (s) =>
        s.student_guid === studentGuid &&
        selectedClassActivities.some((a) => a.guid === s.activity_guid)
    );
  const getStudentProgressSummary = (studentGuid) => {
    const s = studentsWithProgress.find((x) => x.guid === studentGuid);
    return s || null;
  };

  // ---------------------------
  // Render
  // ---------------------------
  return (
    <Box
      sx={{
        p: 3,
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        background: "rgba(255, 255, 255, 0.25)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.4)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `
             radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
             radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%)
           `,
          pointerEvents: "none",
        },
      }}
    >
      <Box sx={{ maxWidth: 1300, mx: "auto" }}>
        {/* Header */}
        <Paper
          sx={{ ...glassCardSx, mb: 3, p: { xs: 2, md: 3 } }}
          elevation={0}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography
                variant={isSmDown ? "h6" : "h4"}
                sx={{ fontWeight: 900 }}
              >
                {role === "admin" ? "Progress Dashboard" : "Staff Progress"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Branch: {data.branches[0]?.name || "Main"}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={role?.toUpperCase() || "STAFF"}
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
              {/* <Tooltip title="Filter / Options">
                <IconButton>
                  <FilterListIcon />
                </IconButton>
              </Tooltip> */}
            </Stack>
          </Stack>
        </Paper>

        <Grid container spacing={3}>
          {/* LEFT: classes list */}
          <Grid
            item
            sx={{
              width: {
                xs: "100%",
                sm: "100%",
                md: "100%",
                lg: "33%",
                xl: "30%",
              },
            }}
          >
            <Paper
              sx={{ ...glassCardSx, maxHeight: "65vh", overflowY: "auto" }}
              elevation={0}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography sx={{ fontWeight: 800 }}>Classes</Typography>
                <Typography variant="body2" color="text.secondary">
                  {visibleClasses.length}
                </Typography>
              </Stack>

              {isSmDown ? (
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <InputLabel id="class-select-label">Class</InputLabel>
                  <Select
                    labelId="class-select-label"
                    label="Class"
                    value={selectedClassGuid || ""}
                    onChange={(e) => setSelectedClassGuid(e.target.value)}
                  >
                    {visibleClasses.map((c) => (
                      <MenuItem key={c.guid} value={c.guid}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}

              <Stack spacing={1}>
                {classesWithProgress.length === 0 ? (
                  <Typography color="text.secondary">
                    No classes available
                  </Typography>
                ) : (
                  classesWithProgress.map((c) => (
                    <Paper
                      key={c.guid}
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        borderRadius: 2,

                        backdropFilter: "blur(16px)",
                        border: "1px solid rgba(255,255,255,0.5)",
                        boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                        cursor: "pointer",
                        background:
                          selectedClassGuid === c.guid
                            ? "rgba(0, 123, 255, 0.12)"
                            : "rgba(231, 241, 254, 0.5)",
                      }}
                      onClick={() => setSelectedClassGuid(c.guid)}
                    >
                      <Avatar
                        sx={{ bgcolor: "primary.main", width: 44, height: 44 }}
                      >
                        {(c.name || "C").split(" ").slice(-1)[0][0]}
                      </Avatar>

                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 800 }}>
                          {c.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {c.totalStudents} students • {c.totalActivities}{" "}
                          activities
                        </Typography>
                      </Box>

                      <Box sx={{ width: 120 }}>
                        <Typography variant="caption" color="text.secondary">
                          Attendance
                        </Typography>
                        <Progress value={c.attendancePercent} />
                        <Typography
                          sx={{ textAlign: "right", fontWeight: 800 }}
                        >
                          {c.attendancePercent}%
                        </Typography>
                        <Divider sx={{ my: 0.5 }} />
                        <Typography variant="caption" color="text.secondary">
                          Activity
                        </Typography>
                        <Progress value={c.activityPercent} />
                        <Typography
                          sx={{ textAlign: "right", fontWeight: 800 }}
                        >
                          {c.activityPercent}%
                        </Typography>
                      </Box>
                    </Paper>
                  ))
                )}
              </Stack>
            </Paper>

            <Paper sx={{ ...glassCardSx, mt: 2 }} elevation={0}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Selected
              </Typography>
              <Typography color="text.secondary">
                {selectedClass?.name || "—"}
              </Typography>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 1 }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Avg marks
                  </Typography>
                  <Typography sx={{ fontWeight: 800 }}>
                    {classesWithProgress.find(
                      (cc) => cc.guid === selectedClassGuid
                    )?.avgMarks ?? "—"}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="caption" color="text.secondary">
                    Students
                  </Typography>
                  <Typography sx={{ fontWeight: 800 }}>
                    {classesWithProgress.find(
                      (cc) => cc.guid === selectedClassGuid
                    )?.totalStudents ?? 0}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* RIGHT: Main content */}
          <Grid
            item
            sx={{
              width: {
                xs: "100%",
                sm: "100%",
                md: "100%",
                lg: "67%",
                xl: "70%",
              },
            }}
          >
            {role === "staff" && visibleClasses.length === 0 ? (
              <Paper sx={{ ...glassCardSx }}>
                <Typography color="text.secondary">
                  No assigned classes found for your account.
                </Typography>
              </Paper>
            ) : role === "admin" ? (
              <>
                {/* Title for selected class */}
                <Paper sx={{ ...glassCardSx, mb: 2 }} elevation={0}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {selectedClass
                      ? `Students — ${selectedClass.name}`
                      : "Select a class"}
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    {selectedClassStudents.length} students
                  </Typography>
                </Paper>

                {/* If class is selected → show students */}
                {selectedClassGuid ? (
                  <Paper
                    sx={{
                      ...glassCardSx,
                      maxHeight: "67.5vh",
                      overflowY: "auto",
                      pr: 1,
                    }}
                    elevation={0}
                  >
                    <Stack spacing={1}>
                      {studentsWithProgress.length === 0 ? (
                        <Typography color="text.secondary">
                          No students in this class.
                        </Typography>
                      ) : (
                        studentsWithProgress.map((s) => (
                          <Paper
                            key={s.guid}
                            sx={{
                              p: 1,
                              borderRadius: 2,
                              background: "rgba(231, 241, 254, 0.5)",
                              backdropFilter: "blur(16px)",
                              border: "1px solid rgba(255,255,255,0.5)",
                              boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              {/* Student avatar + basic info */}
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Avatar
                                  sx={{
                                    bgcolor: "primary.main",
                                    width: { xs: 32, md: 40 },
                                    height: { xs: 32, md: 40 },
                                  }}
                                >
                                  {(s.name || "S")[0]}
                                </Avatar>

                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: { xs: 12, md: 16 },
                                      fontWeight: 800,
                                    }}
                                  >
                                    {s.name}
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {s.completedActivities}/{s.totalActivities}{" "}
                                    activities
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    sx={{ display: "block", mt: 0.3 }}
                                  >
                                    Avg Marks: {s.avgMarks ?? "—"}
                                  </Typography>
                                </Box>
                              </Stack>

                              {/* Attendance + Activity progress bars */}
                              <Stack
                                direction={{ xs: "column", md: "row" }}
                                spacing={1}
                                alignItems="center"
                              >
                                <Box sx={{ width: { xs: 90, md: 120 } }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Attendance
                                  </Typography>
                                  <Progress value={s.attendancePercent} />
                                  <Typography
                                    sx={{ textAlign: "right", fontWeight: 700 }}
                                  >
                                    {s.attendancePercent}%
                                  </Typography>
                                </Box>

                                <Box sx={{ width: { xs: 90, md: 120 } }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Activity
                                  </Typography>
                                  <Progress value={s.activityPercent} />
                                  <Typography
                                    sx={{ textAlign: "right", fontWeight: 700 }}
                                  >
                                    {s.activityPercent}%
                                  </Typography>
                                </Box>
                              </Stack>

                              {/* Menu button */}
                              <IconButton
                                size="small"
                                onClick={(e) => handleOpenMenu(e, s)}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </Stack>
                          </Paper>
                        ))
                      )}
                    </Stack>
                  </Paper>
                ) : (
                  <Paper sx={{ ...glassCardSx }} elevation={0}>
                    <Typography color="text.secondary">
                      Select a class from the left to view its students.
                    </Typography>
                  </Paper>
                )}
              </>
            ) : (
              // STAFF: stacked layout (overview + activities) then students below
              <>
                {/* CLASS OVERVIEW + ACTIVITIES */}
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        ...glassCardSx,
                        maxHeight: "35vh",
                        overflowY: "auto",
                        pr: 1,
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 900 }}>
                            {selectedClass?.name || "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Class overview
                          </Typography>
                        </Box>

                        <Box sx={{ textAlign: "right" }}>
                          <Typography variant="h5" sx={{ fontWeight: 900 }}>
                            {classesWithProgress.find(
                              (cc) => cc.guid === selectedClassGuid
                            )?.attendancePercent ?? 0}
                            %
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Attendance
                          </Typography>
                        </Box>
                      </Stack>

                      <Divider sx={{ mb: 1 }} />

                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 800, mb: 1 }}
                      >
                        Activities
                      </Typography>

                      {selectedClassActivities.length === 0 ? (
                        <Typography color="text.secondary">
                          No activities for this class.
                        </Typography>
                      ) : (
                        <Stack spacing={1}>
                          {selectedClassActivities.map((a) => {
                            const statuses =
                              selectedClassData.activity_student_status.filter(
                                (st) => st.activity_guid === a.guid
                              );
                            const totalStudents =
                              selectedClassStudents.length || 1;
                            const completed = statuses.filter(
                              (st) =>
                                Number(st.is_completed) === 1 &&
                                selectedClassStudents.some(
                                  (s) => s.guid === st.student_guid
                                )
                            ).length;
                            const percent = Math.round(
                              (completed / totalStudents) * 100
                            );
                            return (
                              <Paper
                                key={a.guid}
                                sx={{ p: 1, borderRadius: 1 }}
                              >
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Box>
                                    <Typography sx={{ fontWeight: 800 }}>
                                      {a.title}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Due {a.due_date}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ width: isMdDown ? 110 : 140 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Completed
                                    </Typography>
                                    <Progress value={percent} />
                                    <Typography
                                      sx={{
                                        textAlign: "right",
                                        fontWeight: 800,
                                      }}
                                    >
                                      {percent}%
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Paper>
                            );
                          })}
                        </Stack>
                      )}
                    </Paper>
                  </Grid>

                  {/* STUDENTS BELOW - full width, capped height */}
                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        ...glassCardSx,
                        maxHeight: "45vh",
                        overflowY: "auto",
                        pr: 1,
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          Students
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {selectedClassStudents.length}
                        </Typography>
                      </Stack>

                      <Stack spacing={1}>
                        {studentsWithProgress.length === 0 ? (
                          <Typography color="text.secondary">
                            No students in this class.
                          </Typography>
                        ) : (
                          studentsWithProgress.map((s) => (
                            <Paper key={s.guid} sx={{ p: 1, borderRadius: 2 }}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                >
                                  <Avatar
                                    sx={{
                                      bgcolor: "primary.main",
                                      width: { xs: 30, md: 40 },
                                      height: { xs: 30, md: 40 },
                                    }}
                                  >
                                    {(s.name || "S")[0]}
                                  </Avatar>
                                  <Box>
                                    <Typography
                                      sx={{
                                        fontSize: { xs: 12, md: 18 },
                                        fontWeight: 800,
                                      }}
                                    >
                                      {s.name}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {s.completedActivities}/
                                      {s.totalActivities} activities
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          display: "block",
                                          mt: 0.5,
                                        }}
                                      >
                                        Avg {s.avgMarks ?? "—"}
                                      </Typography>
                                    </Typography>
                                  </Box>
                                </Stack>

                                <Stack
                                  direction={{ xs: "column", md: "row" }}
                                  alignItems="center"
                                  spacing={2}
                                >
                                  <Box sx={{ width: isMdDown ? 100 : 120 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Attendance
                                    </Typography>
                                    <Progress value={s.attendancePercent} />
                                    <Typography
                                      sx={{
                                        textAlign: "right",
                                        fontWeight: 800,
                                      }}
                                    >
                                      {s.attendancePercent}%
                                    </Typography>
                                  </Box>
                                  {/* 
                                  <Divider
                                    orientation="vertical"
                                    flexItem
                                    sx={{ mx: 1 }}
                                  /> */}

                                  <Box sx={{ width: isMdDown ? 100 : 120 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Activity
                                    </Typography>
                                    <Progress value={s.activityPercent} />
                                    <Typography
                                      sx={{
                                        textAlign: "right",
                                        fontWeight: 800,
                                      }}
                                    >
                                      {s.activityPercent}%
                                    </Typography>
                                  </Box>
                                </Stack>
                                {/* anchor menu */}
                                <Box>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleOpenMenu(e, s)}
                                  >
                                    <MoreVertIcon />
                                  </IconButton>
                                </Box>
                              </Stack>
                            </Paper>
                          ))
                        )}
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Anchor menu */}
      <Menu anchorEl={menuAnchorEl} open={openMenu} onClose={handleCloseMenu}>
        <MenuItem onClick={() => openModalFor("summary")}>
          View Progress
        </MenuItem>
        <MenuItem onClick={() => openModalFor("attendance")}>
          View Attendance
        </MenuItem>
        <MenuItem onClick={() => openModalFor("activities")}>
          View Activities
        </MenuItem>
      </Menu>

      {/* Modal */}
      <Dialog open={modalOpen} onClose={closeModal} fullWidth maxWidth="md">
        <DialogTitle>
          {modalType === "attendance" && `Attendance — ${modalStudent?.name}`}
          {modalType === "activities" && `Activities — ${modalStudent?.name}`}
          {modalType === "summary" &&
            `Progress Summary — ${modalStudent?.name}`}
          {modalType === "marks" && `Marks — ${modalStudent?.name}`}
        </DialogTitle>
        <DialogContent dividers>
          {/* Attendance table */}
          {modalType === "attendance" && modalStudent && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Attendance History
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Class</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getAttendanceRecords(modalStudent.guid).map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>{r.date}</TableCell>
                      <TableCell sx={{ textTransform: "capitalize" }}>
                        {r.status}
                      </TableCell>
                      <TableCell>
                        {data.classes.find((c) => c.guid === r.class_guid)
                          ?.name || r.class_guid}
                      </TableCell>
                    </TableRow>
                  ))}
                  {getAttendanceRecords(modalStudent.guid).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No attendance records
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Box sx={{ mt: 2, textAlign: "right" }}>
                <Button onClick={closeModal} variant="contained">
                  Close
                </Button>
              </Box>
            </>
          )}

          {/* Activities & marks */}
          {modalType === "activities" && modalStudent && (
            <>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 2,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  fontWeight: 600,
                }}
              >
                Activity Submissions
              </Typography>

              {/* Responsive Scrollable Table */}
              <Box sx={{ overflowX: "auto", mb: 3 }}>
                <Table size="small" sx={{ minWidth: 600 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: { xs: 12, sm: 14 } }}>
                        Activity
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: 12, sm: 14 } }}>
                        Due
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: 12, sm: 14 } }}>
                        Completed
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: 12, sm: 14 } }}>
                        Marks
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {getActivityStatuses(modalStudent.guid).map((st) => {
                      const act =
                        selectedClassData.activities.find(
                          (a) => a.guid === st.activity_guid
                        ) || {};
                      return (
                        <TableRow key={st.activity_guid}>
                          <TableCell sx={{ fontSize: { xs: 12, sm: 14 } }}>
                            {act.title || st.activity_guid}
                          </TableCell>

                          <TableCell sx={{ fontSize: { xs: 12, sm: 14 } }}>
                            {act.due_date || "—"}
                          </TableCell>

                          <TableCell
                            sx={{
                              fontSize: { xs: 12, sm: 14 },
                              textTransform: "capitalize",
                            }}
                          >
                            {Number(st.is_completed) === 1 ? "Yes" : "No"}
                          </TableCell>

                          <TableCell>
                            {ratingToMarks(st.rating) ?? "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {getActivityStatuses(modalStudent.guid).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No activity submissions
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>

              <Box sx={{ mt: 2, textAlign: "right" }}>
                <Button
                  onClick={closeModal}
                  variant="contained"
                  sx={{ px: { xs: 2, sm: 3 }, py: { xs: 0.7, sm: 1 } }}
                >
                  Close
                </Button>
              </Box>
            </>
          )}

          {/* Marks view (summary) */}
          {modalType === "marks" && modalStudent && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Marks Breakdown
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Activity</TableCell>
                    <TableCell>Marks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getActivityStatuses(modalStudent.guid)
                    .filter((s) => s.marks > 0)
                    .map((st) => {
                      const act =
                        selectedClassActivities.find(
                          (a) => a.guid === st.activity_guid
                        ) || {};

                      return (
                        <TableRow key={st.activity_guid}>
                          <TableCell>{act.title || st.activity_guid}</TableCell>
                          <TableCell>{st.marks}</TableCell>
                        </TableRow>
                      );
                    })}
                  {getActivityStatuses(modalStudent.guid).filter(
                    (s) => s.marks > 0
                  ).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        No marks available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Box sx={{ mt: 2, textAlign: "right" }}>
                <Button onClick={closeModal} variant="contained">
                  Close
                </Button>
              </Box>
            </>
          )}

          {/* Progress summary */}
          {modalType === "summary" && modalStudent && (
            <>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontSize: { xs: "1rem", md: "1.1rem" } }}
              >
                Progress Summary
              </Typography>

              {/* Responsive Progress Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Attendance */}
                <Grid item xs={12} sm={6} md={4}>
                  <Paper sx={{ p: { xs: 2, sm: 2.5 }, textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                      Attendance
                    </Typography>
                    <Typography
                      sx={{ fontWeight: 800, fontSize: { xs: 18, md: 22 } }}
                    >
                      {getStudentProgressSummary(modalStudent.guid)
                        ?.attendancePercent ?? 0}
                      %
                    </Typography>
                    <Progress
                      value={
                        getStudentProgressSummary(modalStudent.guid)
                          ?.attendancePercent ?? 0
                      }
                    />
                  </Paper>
                </Grid>

                {/* Activity */}
                <Grid item xs={12} sm={6} md={4}>
                  <Paper sx={{ p: { xs: 2, sm: 2.5 }, textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                      Activity
                    </Typography>
                    <Typography
                      sx={{ fontWeight: 800, fontSize: { xs: 18, md: 22 } }}
                    >
                      {getStudentProgressSummary(modalStudent.guid)
                        ?.activityPercent ?? 0}
                      %
                    </Typography>
                    <Progress
                      value={
                        getStudentProgressSummary(modalStudent.guid)
                          ?.activityPercent ?? 0
                      }
                    />
                  </Paper>
                </Grid>

                {/* Average Marks */}
                <Grid item xs={12} sm={6} md={4}>
                  <Paper sx={{ p: { xs: 2, sm: 2.5 }, textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                      Avg Marks
                    </Typography>
                    <Typography
                      sx={{ fontWeight: 800, fontSize: { xs: 18, md: 22 } }}
                    >
                      {getStudentProgressSummary(modalStudent.guid)?.avgMarks ??
                        "—"}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Attendance Table */}
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontSize: { xs: "1rem", md: "1.1rem" } }}
              >
                Recent Attendance
              </Typography>

              <Box sx={{ overflowX: "auto", mb: 3 }}>
                <Table size="small" sx={{ minWidth: 500 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {getAttendanceRecords(modalStudent.guid)
                      .slice(0, 8)
                      .map((r, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{r.date}</TableCell>
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {r.status}
                          </TableCell>
                        </TableRow>
                      ))}

                    {getAttendanceRecords(modalStudent.guid).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          No attendance records
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>

              {/* Activity Table */}
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontSize: { xs: "1rem", md: "1.1rem" } }}
              >
                Recent Activities
              </Typography>

              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 600 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Activity</TableCell>
                      <TableCell>Completed</TableCell>
                      <TableCell>Marks</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {getActivityStatuses(modalStudent.guid)
                      .slice(0, 8)
                      .map((st) => {
                        const act =
                          selectedClassData.activities.find(
                            (a) => a.guid === st.activity_guid
                          ) || {};
                        return (
                          <TableRow key={st.activity_guid}>
                            <TableCell>
                              {act.title || st.activity_guid}
                            </TableCell>
                            <TableCell>
                              {Number(st.is_completed) === 1 ? "Yes" : "No"}
                            </TableCell>
                            <TableCell>
                              {ratingToMarks(st.rating) ?? "—"}
                            </TableCell>
                          </TableRow>
                        );
                      })}

                    {getActivityStatuses(modalStudent.guid).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          No activities
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>

              <Box sx={{ mt: 3, textAlign: "right" }}>
                <Button onClick={closeModal} variant="contained" sx={{ px: 3 }}>
                  Close
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
